import React, { memo } from 'react';
import '@xyflow/react/dist/style.css';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { Shield, EyeOff, FileText, CreditCard, Layers, Wallet, Activity, Leaf } from 'lucide-react';
const GridCell = ({
  icon: Icon,
  label,
  value,
  statusColor
}: {
  icon: any;
  label: string;
  value: string;
  statusColor?: string;
}) => {
  const dotColor = statusColor === 'text-amber-500' ? 'bg-amber-500' : 'bg-emerald-500';
  return <div className="flex items-center gap-2.5 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200" data-id="element-84">
      <Icon className={`w-4 h-4 ${statusColor || 'text-gray-400'} flex-shrink-0`} data-id="element-85" />
      <span className="text-gray-600 text-[12px] font-medium" data-id="element-86">{label}</span>
      <div className="ml-auto flex items-center gap-1.5" data-id="element-87">
        <div className={`w-1.5 h-1.5 rounded-full ${dotColor}`} data-id="element-88" />
        <span className="text-[11px] font-medium text-gray-600" data-id="element-89">{value}</span>
      </div>
    </div>;
};
const EngineNode = ({
  selected,
  data
}: NodeProps<any>) => {
  const focusedService = data?.focusedService;
  const providerAggregate = data?.providerAggregate;
  const hasFocus = !!focusedService;
  const hasProviderFocus = !!providerAggregate;
  const complianceValue = hasFocus ? focusedService.compliance : hasProviderFocus ? providerAggregate.compliance : 81;
  const complianceOk = complianceValue >= 80;
  const totalRequests = hasFocus ? focusedService.requestsPerDay?.toLocaleString() : hasProviderFocus ? providerAggregate.requestsPerDay.toLocaleString() : '17,080';
  const totalCost = hasFocus ? `€${focusedService.cost}` : hasProviderFocus ? `€${providerAggregate.cost}` : '€214';
  const serviceCount = hasFocus ? '1' : hasProviderFocus ? String(providerAggregate.count) : '8';
  // ECO Impact: kg CO₂ emitted by AI consumption per month
  const ecoImpact = hasFocus ? focusedService.ecoScore ?? 12 : hasProviderFocus ? providerAggregate.ecoScore ?? 38 : 42;
  const ecoLabel = `${ecoImpact} kg`;
  const ecoIsLow = ecoImpact < 20;
  return <div className={`
        relative p-6 rounded-lg w-[900px] transition-all duration-300
        bg-white border shadow-lg
        ${selected ? 'border-[#00B4D8] ring-1 ring-[#00B4D8]' : 'border-gray-200'}
      `} data-id="element-90">
      <Handle type="target" position={Position.Top} className="!bg-[#00B4D8] !w-5 !h-2 !rounded-sm !border-0" data-id="element-91" />
      <Handle type="source" position={Position.Bottom} className="!bg-[#00B4D8] !w-5 !h-2 !rounded-sm !border-0" data-id="element-92" />

      {/* Title */}
      <div className="flex items-center gap-3 mb-5" data-id="element-93">
        <h2 className="text-[#2a3a54] font-semibold text-[18px] tracking-tight" data-id="element-94">
          DMOOD ENGINE
        </h2>
        <span className="text-gray-500 text-[11px] uppercase tracking-wider hidden sm:inline" data-id="element-95">
          {hasFocus ? `Filtered: ${focusedService.name}` : hasProviderFocus ? `Provider: ${providerAggregate.count} services` : 'Governance & Orchestration'}
        </span>
      </div>

      {/* Key Metrics — large, prominent */}
      <div className="grid grid-cols-5 gap-3 mb-5" data-id="element-96">
        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-97">
          <div className="flex items-center gap-1.5 mb-1.5" data-id="element-98">
            <Layers className="w-3.5 h-3.5 text-gray-400" data-id="element-99" />
            <span className="text-[10px] text-gray-500 uppercase tracking-wider" data-id="element-100">
              Services
            </span>
          </div>
          <span className="text-[22px] font-bold text-[#2a3a54] leading-none" data-id="element-101">
            {serviceCount}
          </span>
        </div>
        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-102">
          <div className="flex items-center gap-1.5 mb-1.5" data-id="element-103">
            <Wallet className="w-3.5 h-3.5 text-gray-400" data-id="element-104" />
            <span className="text-[10px] text-gray-500 uppercase tracking-wider" data-id="element-105">
              Costo Mensile
            </span>
          </div>
          <span className="text-[22px] font-bold text-[#2a3a54] leading-none" data-id="element-106">
            {totalCost}
          </span>
        </div>
        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-107">
          <div className="flex items-center gap-1.5 mb-1.5" data-id="element-108">
            <Activity className="w-3.5 h-3.5 text-gray-400" data-id="element-109" />
            <span className="text-[10px] text-gray-500 uppercase tracking-wider" data-id="element-110">
              Requests/Day
            </span>
          </div>
          <span className="text-[22px] font-bold text-[#2a3a54] leading-none" data-id="element-111">
            {totalRequests}
          </span>
        </div>
        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-112">
          <div className="flex items-center gap-1.5 mb-1.5" data-id="element-113">
            <Leaf className="w-3.5 h-3.5 text-gray-400" data-id="element-114" />
            <span className="text-[10px] text-gray-500 uppercase tracking-wider" data-id="element-115">
              ECO Impact
            </span>
          </div>
          <span className="text-[22px] font-bold text-[#2a3a54] leading-none" data-id="element-116">
            {ecoLabel}
          </span>
        </div>
        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-117">
          <div className="flex items-center gap-1.5 mb-1.5" data-id="element-118">
            <Shield className="w-3.5 h-3.5 text-gray-400" data-id="element-119" />
            <span className="text-[10px] text-gray-500 uppercase tracking-wider" data-id="element-120">
              Compliance
            </span>
          </div>
          <span className={`text-[22px] font-bold leading-none ${complianceOk ? 'text-emerald-500' : 'text-amber-500'}`} data-id="element-121">
            {complianceValue}%
          </span>
        </div>
      </div>

      {/* Modules — compact, secondary */}
      <div className="border-t border-gray-200 pt-4" data-id="element-122">
        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2.5 block" data-id="element-123">
          Moduli Attivi
        </span>
        <div className="grid grid-cols-4 gap-2" data-id="element-124">
          <GridCell icon={Shield} label="Policy Check" value="Active" statusColor="text-emerald-500" data-id="element-125" />
          <GridCell icon={EyeOff} label="PII Masking" value="Active" statusColor="text-emerald-500" data-id="element-126" />
          <GridCell icon={FileText} label="Logging & Audit" value="Active" statusColor="text-emerald-500" data-id="element-127" />
          <GridCell icon={CreditCard} label="Cost Control" value="Warning" statusColor="text-amber-500" data-id="element-128" />
        </div>
      </div>
    </div>;
};
export default memo(EngineNode);